import { Button, Skeleton, Tooltip } from "antd";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import classNames from "classnames";
import { getCookie } from "cookies-next";
import Link from "next/link";

import { poppins } from "@/components/common/fonts";

import { IconQuestionCircle } from "../../Icons";

import { QuantityInput } from "./QuantityInput";

type Product = {
  isSelected?: boolean;
  title?: ReactNode;
  productTitle?: ReactNode;
  productPrice?: ReactNode;
  isMonthly?: boolean;
  isBilledAnnually?: boolean;
  isVisibleQuantity?: boolean;
  isDisabledQuantity?: boolean;
  quantity?: number;
  unit?: ReactNode;
  min?: number;
  max?: number;
  descriptions?: ReactNode[];
};

export type PurchaseContentProps = {
  title?: ReactNode;
  productsTitle?: ReactNode;
  productTitleGradient?: boolean;
  productTitleIcon?: ReactNode;
  productsSubTitle?: ReactNode;
  products?: Product[];
  productsDescriptions?: ReactNode[];

  currentPlan?: ReactNode;
  currentBillingCycle?: {
    start: Date;
    end: Date;
  };
  nextBillingCycle?: {
    start: Date;
    end: Date;
  };

  billingCycleCautionTitle?: ReactNode;
  billingCycleCautionDescription?: ReactNode;

  totalPrice?: number;
  details?: {
    title?: ReactNode;
    description?: ReactNode;
    price?: number;
    noDiscount?: boolean;
  }[];
  prorationDetails?: {
    title?: ReactNode;
    description?: ReactNode;
    price?: number;
    noDiscount?: boolean;
  }[];
  todayPrice?: number;
  loadingTodayPrice?: boolean;
  nextBillingPrice?: number;
  nextBillingDate?: Date;
  discount?:
    | {
        title?: string;
        amount?: number;
      }
    | null
    | undefined;

  onChangeSelectedProduct?: (index: number) => void;
  onChangeProductQuantity?: (index: number, quantity: number) => void;

  confirmText?: ReactNode;
  disabledConfirm?: boolean;
  loadingConfirm?: boolean;
  onConfirm?: () => void;
  goToBillingPortal?: () => void;
};

export const PurchaseContent = ({
  title,
  productsTitle,
  productTitleGradient,
  productTitleIcon,
  productsSubTitle,
  products,
  productsDescriptions,
  currentPlan,
  currentBillingCycle,
  nextBillingCycle,
  billingCycleCautionTitle,
  billingCycleCautionDescription,
  totalPrice,
  details,
  prorationDetails,
  todayPrice,
  loadingTodayPrice,
  nextBillingPrice,
  nextBillingDate,
  discount,

  onChangeSelectedProduct,
  onChangeProductQuantity,

  confirmText,
  disabledConfirm,
  loadingConfirm,
  onConfirm,
  goToBillingPortal,
}: PurchaseContentProps) => {
  const { t } = useTranslation("common");

  const locale = (getCookie("NEXT_LOCALE") as string) || "en";

  const formatDate = useMemo(() => {
    const intl = new Intl.DateTimeFormat(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    return intl.format;
  }, [locale]);

  return (
    <Container>
      <Header>
        <Title>{title}</Title>
      </Header>

      <Content>
        <Products>
          <ProductHeader>
            <PricingTitle>
              <PricingTitleText
                className={classNames({
                  gradient: productTitleGradient,
                })}
              >
                {productsTitle}
              </PricingTitleText>

              {productTitleIcon && (
                <PricingTitleIcon>{productTitleIcon}</PricingTitleIcon>
              )}
            </PricingTitle>

            <ProductSubTitle>{productsSubTitle}</ProductSubTitle>
          </ProductHeader>

          <PricingList>
            {products?.map((product, productIndex) => {
              return (
                <PricingItem
                  key={productIndex}
                  onClick={() => onChangeSelectedProduct?.(productIndex)}
                  className={classNames({
                    selected: product.isSelected,
                  })}
                >
                  {!!product.title && (
                    <PricingItemTitle>{product.title}</PricingItemTitle>
                  )}

                  <PricingItemContent>
                    <PricingDetails>
                      <p>{product.productTitle}</p>
                      <p>
                        {product.productPrice}{" "}
                        {product.isMonthly && (
                          <span className="payment-period">
                            / {t("mo")}{" "}
                            {product.isBilledAnnually &&
                              `(${t("Billed Annually")})`}
                          </span>
                        )}
                      </p>
                    </PricingDetails>

                    {product.isVisibleQuantity && (
                      <PricingItemQuantity>
                        <QuantityInput
                          value={product.quantity}
                          unit={product.unit}
                          onChange={(value) => {
                            onChangeProductQuantity?.(productIndex, value);
                          }}
                          min={product.min}
                          max={product.max}
                          disabled={product.isDisabledQuantity}
                        />
                      </PricingItemQuantity>
                    )}
                  </PricingItemContent>

                  <PricingItemDescriptions>
                    {product.descriptions?.map(
                      (description, descriptionIndex) => {
                        return (
                          <PricingSchedule key={descriptionIndex}>
                            {description}
                          </PricingSchedule>
                        );
                      },
                    )}
                  </PricingItemDescriptions>
                </PricingItem>
              );
            })}
          </PricingList>

          <ProductsDescriptions>
            {productsDescriptions?.map((description, key) => {
              return (
                <ProductDescription key={key}>{description}</ProductDescription>
              );
            })}
          </ProductsDescriptions>
        </Products>

        <SectionDivider />

        <Details>
          <DetailList>
            <DetailItem>
              <DetailItemTitle>{t("Your Plan")}</DetailItemTitle>
              <DetailItemContent>{currentPlan}</DetailItemContent>
            </DetailItem>

            <DetailItem>
              <DetailItemTitle>{t("Current Billing Cycle")}</DetailItemTitle>
              <DetailItemContent>
                {formatDate(currentBillingCycle?.start)} -{" "}
                {formatDate(currentBillingCycle?.end)}
              </DetailItemContent>
            </DetailItem>

            {!loadingTodayPrice && !!nextBillingCycle && (
              <DetailItem>
                <DetailItemTitle>{t("Next Billing Cycle")}</DetailItemTitle>
                <DetailItemContent>
                  {formatDate(nextBillingCycle?.start)} -{" "}
                  {formatDate(nextBillingCycle?.end)}
                </DetailItemContent>
              </DetailItem>
            )}
          </DetailList>

          {(!!billingCycleCautionTitle || !!billingCycleCautionDescription) && (
            <BillingCycleCaution>
              {!!billingCycleCautionTitle && (
                <BillingCycleCautionTitle>
                  {billingCycleCautionTitle}
                </BillingCycleCautionTitle>
              )}
              {!!billingCycleCautionDescription && (
                <BillingCycleCautionDescription>
                  {billingCycleCautionDescription}
                </BillingCycleCautionDescription>
              )}
            </BillingCycleCaution>
          )}

          <Summary>
            <SummaryRow>
              <SummaryLabel>{t("Total")}</SummaryLabel>
              <SummaryValue className="bold">
                {loadingTodayPrice ? (
                  <StyledSkeleton active size="small" />
                ) : (
                  <>${totalPrice?.toFixed(2)}</>
                )}
              </SummaryValue>
            </SummaryRow>

            <SummaryHorizontalBar />

            {loadingTodayPrice ? (
              <SummaryRow>
                <StyledSkeleton
                  active
                  size="small"
                  css={`
                    &&& {
                      width: 100%;
                    }
                  `}
                />
              </SummaryRow>
            ) : (
              <>
                {details?.map((detail, key) => (
                  <SummaryRow key={key}>
                    <SummaryLabel>
                      {detail.title}
                      {discount &&
                        (detail.price ?? 0) > 0 &&
                        !detail.noDiscount && (
                          <span className="discount">({discount.title})</span>
                        )}
                      <br />
                      {detail.description}
                    </SummaryLabel>
                    <SummaryValue>${detail.price?.toFixed(2)}</SummaryValue>
                  </SummaryRow>
                ))}

                {prorationDetails && prorationDetails.length > 0 && (
                  <ProrationDetails>
                    <ProrationDetailsTitle>
                      {t("Proration")}
                      <Tooltip
                        title={t(
                          "Adjusted based on the time left in your current billing cycle.",
                        )}
                        mouseEnterDelay={0.5}
                        placement="bottom"
                      >
                        <IconQuestionCircle className="icon" />
                      </Tooltip>
                    </ProrationDetailsTitle>

                    <ProrationDetailsContent>
                      {prorationDetails.map((detail, key) => (
                        <SummaryRow key={key}>
                          <SummaryLabel className="thin">
                            {detail.title}
                            <br />
                            {detail.description}
                          </SummaryLabel>
                          <SummaryValue>
                            {(detail.price ?? 0) < 0 && "-"}$
                            {Math.abs(detail.price ?? 0).toFixed(2)}
                          </SummaryValue>
                        </SummaryRow>
                      ))}
                    </ProrationDetailsContent>
                  </ProrationDetails>
                )}
              </>
            )}

            <SummaryHorizontalBar />

            <SummaryRow>
              <SummaryLabel>{t("Due today")}</SummaryLabel>
              <SummaryValue className="bold">
                {loadingTodayPrice ? (
                  <StyledSkeleton active size="small" />
                ) : (
                  <>${todayPrice?.toFixed(2)}</>
                )}
              </SummaryValue>
            </SummaryRow>

            {loadingTodayPrice ? (
              <SummaryRow>
                <StyledSkeleton
                  active
                  size="small"
                  css={`
                    &&& {
                      width: 100%;
                    }
                  `}
                />
              </SummaryRow>
            ) : (
              <SummaryRow>
                <SummaryLabel>
                  {t("Due on {{date}}", {
                    date: formatDate(nextBillingDate),
                  })}
                </SummaryLabel>
                <SummaryValue className="bold">
                  {loadingTodayPrice ? (
                    <StyledSkeleton active size="small" />
                  ) : (
                    <>${nextBillingPrice?.toFixed(2)}</>
                  )}
                </SummaryValue>
              </SummaryRow>
            )}
          </Summary>

          <ConfirmButton
            type="primary"
            loading={loadingConfirm || loadingTodayPrice}
            disabled={disabledConfirm}
            onClick={onConfirm}
          >
            {confirmText}
          </ConfirmButton>

          <Link href="#" onClick={goToBillingPortal}>
            {t("Manage billing info")}
          </Link>
        </Details>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  padding: 24px 30px 30px 30px;

  .ant-modal-content:has(&) {
    min-width: 580px;
  }
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.div`
  color: #333;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 175% */
`;

const Content = styled.div`
  flex-grow: 1;

  display: flex;
  gap: 20px;

  @media (max-width: 940px) {
    flex-direction: column;
  }
`;

const SectionDivider = styled.div`
  width: 1px;
  height: auto;
  background: #ebebeb;

  @media (max-width: 940px) {
    width: auto;
    height: 1px;
  }
`;

const Products = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
`;

const ProductHeader = styled.div`
  margin-bottom: 30px;
`;

const PricingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-bottom: 10px;

  ${poppins.style}
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 100% */
  color: #262626;

  & > svg {
    flex-shrink: 0;
  }
`;

const ProductSubTitle = styled.div`
  color: #4b4f58;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 138.462% */
`;

/**
 * - &.gradient
 */
const PricingTitleText = styled.span`
  &.gradient {
    background: linear-gradient(90deg, #00cbff 0%, #4276ee 100%);
    background-clip: text;
    color: transparent;
  }
`;

const PricingTitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  color: #4276ee;

  & > svg {
    width: 24px;
  }
`;

const PricingList = styled.div`
  margin-bottom: 20px;
`;

const ProductsDescriptions = styled.ul`
  margin: 0;
  padding: 10px;
  border-radius: 4px;
  background: #fbfbfb;
  list-style-position: inside;
`;

const ProductDescription = styled.li`
  color: #4b4f58;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

/**
 * - &.selected
 */
const PricingItem = styled.div`
  padding: 20px;
  border-radius: 4px;
  border: 1px solid #eaeaea;
  opacity: 0.5;
  background: #fff;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  cursor: pointer;

  transition: background 0.3s, opacity 0.3s, border-color 0.3s, box-shadow 0.3s;

  &.selected {
    padding: 19px;
    background: #fbfbfb;
    border: 2px solid #4276ee;
    opacity: 1;
  }

  &:hover {
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  }
`;

const PricingItemTitle = styled.div`
  margin-bottom: 10px;

  color: #333;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const PricingItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 10px;
  flex-wrap: wrap;
`;

const PricingItemDescriptions = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
`;

/**
 * - & .payment-period
 */
const PricingDetails = styled.div`
  p {
    margin: 0;
  }

  margin-bottom: 14px;

  color: #333;
  ${poppins.style}
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  .payment-period {
    color: #999;
    font-size: 13px;
    font-weight: 400;
  }
`;

const PricingSchedule = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #4b4f58;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 138.462% */

  & > * {
    flex-shrink: 0;
  }
`;

const PricingItemQuantity = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 150px;

  display: flex;
  justify-content: end;
`;

const Details = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 360px;
`;

const DetailList = styled.div`
  padding: 0px 10px;

  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailItemTitle = styled.div`
  margin-bottom: 5px;

  color: #333;
  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 138.462% */
`;

const DetailItemContent = styled.div`
  color: #4b4f58;
  ${poppins.style}
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const Summary = styled.div`
  display: flex;
  padding: 14px 10px;
  flex-direction: column;
  gap: 14px;

  border-radius: 4px;
  background: #fbfbfb;

  margin-bottom: 20px;
`;

const ProrationDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * - & > .icon
 */
const ProrationDetailsTitle = styled.div`
  display: flex;
  align-items: center;

  color: #999;
  ${poppins.style}
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  & > .icon {
    cursor: pointer;
  }
`;

const ProrationDetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
  flex-wrap: wrap;
`;

/**
 * - &.thin
 * - & .discount
 */
const SummaryLabel = styled.div`
  color: #333;
  ${poppins.style}
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 128.571% */

  &.thin {
    font-weight: 400;
  }

  & .discount {
    margin-left: 4px;
  }
`;

/**
 * - &.bold
 */
const SummaryValue = styled.div`
  color: #4b4f58;
  ${poppins.style}
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &.bold {
    color: #333;
    font-weight: 500;
    line-height: 18px; /* 128.571% */
  }
`;

const StyledSkeleton = styled(Skeleton.Input)`
  &&& {
    height: 17px;
  }

  &&& span {
    height: 17px;
  }
`;

const SummaryHorizontalBar = styled.div`
  width: 100%;
  height: 1px;
  background: #ebebeb;
`;

const BillingCycleCaution = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  padding: 10px;
  border-radius: 4px;
  border: 1px solid #f6791f;
  background: #fcf4db;
  margin-bottom: 20px;

  ${poppins.style}
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const BillingCycleCautionTitle = styled.div`
  color: #333;
  font-weight: 500;
  line-height: 18px; /* 138.462% */
`;

const BillingCycleCautionDescription = styled.div`
  color: #4b4f58;
  white-space: pre-wrap;
`;

const ConfirmButton = styled(Button)`
  align-self: stretch;

  height: 44px;
  border-radius: 4px;
  background: #4276ee;

  width: 100%;
  margin-bottom: 14px;

  color: #fff;
  ${poppins.style}
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`;
